import { Button, Group, Paper, Text } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faGrid2Plus,
  faListCheck,
  faUser,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useAllow } from '../../../hooks/useAllow';
import styles from './StudioCard.module.css';
import type_ from './settingsType.module.css';

interface StudioLink {
  readonly label: string;
  readonly path: string;
  readonly icon: IconDefinition;
  /** Scope required to reach the route; omitted when the route is open. */
  readonly scope?: string;
}

/**
 * Only the caller's *own* Studio surfaces belong here. The admin areas
 * (Agents, Skills, Tools, Integrations) are tenant configuration and are
 * reached from Moo Studio's own nav.
 */
const LINKS: readonly StudioLink[] = [
  { label: 'My Agents', path: '/moo/studio/my-agents', icon: faUser },
  { label: 'My Skills', path: '/moo/studio/custom-skills', icon: faBolt },
  {
    label: 'My Tasks',
    path: '/moo/studio/tasks',
    icon: faListCheck,
    // OMNI-382 restricts the tasks route; without this the button would
    // silently bounce the user to /moo/studio/agents.
    scope: 'account:cowbell',
  },
];

export interface StudioCardProps {
  /** Switches to the Apps pane. My Apps lives in this dialog, so it moves
   *  panes rather than navigating away like the Studio links do. */
  readonly onOpenApps?: () => void;
  /** Closes the dialog and navigates. Leaving the conversation is a real
   *  context switch, so the host owns it. */
  readonly onNavigate?: (path: string) => void;
}

/**
 * Entry points into the things a user builds for themselves in Moo Studio.
 *
 * Sits in Personalization because these are the caller's own agents, skills
 * and tasks — the same reasoning that puts Memory and Tones here.
 */
export function StudioCard({ onNavigate, onOpenApps }: StudioCardProps) {
  const canSeeTasks = useAllow(['account:cowbell']);
  const visible = LINKS.filter((link) => !link.scope || canSeeTasks);

  if (!onNavigate || visible.length === 0) return null;

  return (
    <Paper withBorder className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <FontAwesomeIcon
            icon={faWandMagicSparkles}
            className={styles.titleIcon}
          />
          <Text className={type_.cardTitle}>Make OMNI your own</Text>
        </div>
        <Text className={`${type_.subtitle} ${styles.subtitle}`}>
          Moo Studio is where you build the things only you need — an agent that
          knows your accounts, a skill you reuse every week, a task that runs
          without you. Opening one leaves this conversation.
        </Text>
      </div>
      <Group gap="sm" wrap="wrap" className={styles.actions}>
        {onOpenApps && (
          <Button
            variant="default"
            size="sm"
            leftSection={<FontAwesomeIcon icon={faGrid2Plus} />}
            onClick={onOpenApps}
          >
            My Apps
          </Button>
        )}
        {visible.map((link) => (
          <Button
            key={link.path}
            variant="default"
            size="sm"
            leftSection={<FontAwesomeIcon icon={link.icon} />}
            onClick={() => onNavigate(link.path)}
          >
            {link.label}
          </Button>
        ))}
      </Group>
    </Paper>
  );
}
